import { BookOpen, Lock } from 'lucide-react';

import { Html } from './ProblemDescription';

import { useEditorial } from '@/lib/hooks/useEditorial';

interface Props {
  slug: string;
  /**
   * Whether this caller has earned the editorial. Gates the FETCH, not just the
   * render: the server answers an unsolved student with a 403, and a failed
   * request in the network tab advertises that something is being withheld.
   */
  unlocked: boolean;
}

export function EditorialTab({ slug, unlocked }: Props) {
  const { data, isLoading, isError } = useEditorial(slug, unlocked);

  // Defensive: the tab is only mounted when unlocked, but keeping the locked
  // copy here means a future caller that renders it eagerly still explains
  // itself instead of showing an empty panel.
  if (!unlocked) {
    // "Solve this problem to unlock the editorial" was true when solving was
    // the only way in. It is now a false sentence read by exactly the wrong
    // student: the one who is stuck, whose trainer is about to debrief the lab
    // and release the walkthrough. Both routes in are named.
    return (
      <Empty icon={<Lock className="h-5 w-5" />} title="Editorial locked">
        Solve this problem to unlock the walkthrough — or wait for your trainer to release it
        when they debrief this topic.
      </Empty>
    );
  }

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading editorial…</div>;
  }

  // A 404 here is the ordinary case for most of the catalogue: only the
  // authored curriculum problems carry an editorial. It is not an error worth
  // shouting about, so it reads as an absence rather than a failure.
  if (isError || !data?.editorial) {
    return (
      <Empty icon={<BookOpen className="h-5 w-5" />} title="No editorial yet">
        Nobody has written a walkthrough for this problem.
      </Empty>
    );
  }

  return (
    // min-w-0 + break-words keep long identifiers and URLs from widening the
    // pane; [&_pre]:overflow-x-auto confines a wide code block's scrolling to
    // the block itself. Together these are what stop the 390px student portal
    // from scrolling sideways.
    <div className="h-full min-w-0 overflow-y-auto px-4 py-4">
      <h2 className="mb-3 flex items-center gap-2 text-[15px] font-semibold">
        <BookOpen className="h-4 w-4 text-primary" />
        Editorial
      </h2>
      <Html
        html={data.editorial}
        className="min-w-0 break-words [&_pre]:overflow-x-auto [&_pre]:max-w-full"
      />
    </div>
  );
}

function Empty({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      <span className="text-muted-foreground">{icon}</span>
      <p className="text-sm font-medium">{title}</p>
      <p className="max-w-xs text-sm text-muted-foreground">{children}</p>
    </div>
  );
}
